import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchBillingStatus } from './billingApi';

// Shared hook that exposes the account's Active Revenue Opportunity quota to
// any activation surface (Revenue Finder cards, Segment Optimizer chat, etc.).
//
// FAIL-OPEN by design: if /billing/status errors, times out, or there is no
// billing session (e.g. app opened outside Shopify, or the billing backend is
// unreachable), we DO NOT block activation. `canActivate` defaults to true and
// stays true on failure. The hard cap lives server-side (billing/routes.py is
// the source of truth); this hook is UX-only — it greys out the extra
// opportunities so merchants SEE the value they're leaving on the table
// ("UFF moment") without ever hiding it, and never accidentally locks a paying
// user out because a status call flaked.
//
// BACKLOG (server-side enforcement): the activation endpoints
// (/levers/activate-klaviyo, /segment-chat/activate-klaviyo) must also validate
// the quota server-side and reject over-cap activations with a clear error, so
// this fail-open UI can never be bypassed. Not implemented here (frontend-only).

// Tiny in-memory cache so we don't refetch on every mount/render while the user
// navigates between views in the same session. Short TTL keeps it fresh after
// an activation frees/uses a slot; refresh() bypasses it.
const CACHE_TTL_MS = 30 * 1000;
let cache = null; // { data, ts }

// PAUSADO (decision de Alejo 2026-07-14): el modelo de cobro cambio y la UI de
// cupos/candados NO debe verse en produccion. Todo el sistema queda inerte
// salvo que el build lleve REACT_APP_ENABLE_BILLING=true (mismo flag que el
// resto del UI de billing). El trabajo activo completo vive en la rama
// `billing-quota-ui` para retomarlo cuando se defina el nuevo modelo.
const QUOTA_UI_ENABLED = process.env.REACT_APP_ENABLE_BILLING === 'true';
const INERT_STATE = {
  quotaTotal: null, activeCount: null, remainingSlots: null,
  canActivate: true, atCapacity: false, hasQuotaData: false,
  loading: false, error: null, refresh: () => {},
};

export default function useBillingQuota({ token } = {}) {
  const [state, setState] = useState(() => {
    if (cache && Date.now() - cache.ts < CACHE_TTL_MS) {
      return { data: cache.data, loading: false, error: null };
    }
    return { data: null, loading: true, error: null };
  });
  const mountedRef = useRef(true);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!QUOTA_UI_ENABLED) return; // pausado: nunca llamar a la API
    if (!force && cache && Date.now() - cache.ts < CACHE_TTL_MS) {
      if (mountedRef.current) setState({ data: cache.data, loading: false, error: null });
      return;
    }
    if (mountedRef.current) setState((s) => ({ ...s, loading: true }));
    try {
      const data = await fetchBillingStatus({ token });
      cache = { data, ts: Date.now() };
      if (mountedRef.current) setState({ data, loading: false, error: null });
    } catch (err) {
      // Fail-open: keep whatever we had (or null) and record the error, but
      // never surface it as a block. Callers treat null/error as "no limit".
      if (mountedRef.current) setState((s) => ({ ...s, loading: false, error: err }));
    }
  }, [token]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  const refresh = useCallback(() => load({ force: true }), [load]);

  const data = state.data;
  const quotaTotal = data?.quota_total ?? null;
  const activeCount = data?.active_opportunities ?? null;
  const remainingSlots = data?.remaining_slots ?? null;

  // canActivate is TRUE unless the backend explicitly says otherwise. Any
  // missing data (no session, load error, still loading) => allow (fail-open).
  const canActivate = data ? data.can_activate !== false : true;
  const atCapacity = data ? remainingSlots === 0 : false;

  // PAUSADO: con el flag apagado, el hook corre (orden de hooks intacto) pero
  // devuelve siempre el estado inerte — cero UI de cupos, cero llamadas a la API.
  if (!QUOTA_UI_ENABLED) return INERT_STATE;

  return {
    quotaTotal,
    activeCount,
    remainingSlots,
    canActivate,
    atCapacity,
    loading: state.loading,
    error: state.error,
    hasQuotaData: Boolean(data),
    refresh,
  };
}
